import { lessThanOrEqual } from "dinero.js";
import React from "react";
import { defaults } from "tsi-common-react/src/apps/checkout/defaults";
import { FinancingModalTriggerBannerMinicart } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { Link } from "tsi-common-react/src/components/Link/index";
import { SrFootnote } from "tsi-common-react/src/components/SrFootnote/index";
import { useMountEffect } from "tsi-common-react/src/hooks/useMountEffect";
import { IBasket } from "tsi-common-react/src/models/catalogue";
import { isoMonths } from "tsi-common-react/src/models/nominals";
import { useAppSelector } from "tsi-common-react/src/store/reducers";
import { listFinancingPlans } from "tsi-common-react/src/utils/financing";
import { format } from "tsi-common-react/src/utils/format";
import {
    type Dinero,
    getDinero,
    getMonthlyPrice,
    toUnit,
} from "tsi-common-react/src/utils/money";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { c, t } from "ttag";

import styles from "./index.module.scss";

interface FinancingTerms {
    termLength: string;
    termSuperscript: string;
    termThreshold: string;
}

const calculateFinancingTerms = (basket: IBasket | null): FinancingTerms => {
    const total = basket ? getDinero(basket.total_excl_tax) : getDinero(0);

    const plans = listFinancingPlans();
    const availablePlans = plans.filter((plan) => {
        return lessThanOrEqual(plan.threshold, total);
    });

    if (availablePlans.length) {
        const bestPlan = availablePlans[availablePlans.length - 1];

        return {
            termLength: isoMonths.unwrap(bestPlan.length).toString(),
            termSuperscript: bestPlan.superscript,
            termThreshold: toUnit(bestPlan.threshold).toString(),
        };
    }

    return {
        termLength: "1",
        termSuperscript: "",
        termThreshold: "",
    };
};

const calculateMonthlyPrice = (
    basket: IBasket | null,
    termLength: string,
): Dinero => {
    return basket
        ? getMonthlyPrice(
              getDinero(basket.total_excl_tax),
              parseInt(termLength, 10),
          )
        : getDinero(0.0);
};

const shouldShowFinancing = (
    basket: IBasket | null,
    termThreshold: string,
): boolean => {
    return basket && termThreshold
        ? parseFloat(basket.total_excl_tax) >= parseFloat(termThreshold)
        : false;
};

// TODO: Consolidate Financing Trigger components into one
export const FinancingModalTriggerMinicart = (props: {
    closeModal: () => void;
}): React.ReactNode => {
    const basket = useAppSelector((rootState) => {
        const state = rootState.checkout || defaults;
        return state.data.basket;
    });

    const financeLink = usePageURL("finance_link");

    const [financingTerms, setFinancingTerms] = React.useState<FinancingTerms>({
        termLength: "1",
        termSuperscript: "",
        termThreshold: "",
    });

    useMountEffect(() => {
        setFinancingTerms(calculateFinancingTerms(basket));
    });

    const pricePerMonth = calculateMonthlyPrice(
        basket,
        financingTerms.termLength,
    );
    const showFinancing = shouldShowFinancing(
        basket,
        financingTerms.termThreshold,
    );

    return (
        <div className={styles.basketPopoverFinancing}>
            {showFinancing && (
                <>
                    <div className={styles.basketPopoverFinancingTitle}>
                        {t`or Special Financing`}
                    </div>
                    {/* TODO(i18n): Use jt to keep complete sentence together with interpolated components. The entire financing terms message "$50/mo² for 24 Months³" should be one translatable string with the superscript links interpolated as components, not split into fragments like "${price}/{t`mo`} {t`for`} {t`Months`}" */}
                    <div className={styles.basketPopoverFinancingTerms}>
                        {format.money(pricePerMonth)}/{t`mo`}
                        <sup>
                            <Link href={financeLink}>
                                2
                                <SrFootnote
                                    text={t`Additional information about Terms and Conditions`}
                                />
                            </Link>
                        </sup>
                        <span
                            className={styles.basketPopoverFinancingTermsMedium}
                        >
                            {t`for`}
                        </span>
                        {c("FinancingModalTriggerMinicart")
                            .t`${financingTerms.termLength} Months`}
                        <sup>
                            <Link href={financeLink}>
                                {financingTerms.termSuperscript}
                                <SrFootnote
                                    text={t`Additional information about Terms and Conditions`}
                                />
                            </Link>
                        </sup>
                    </div>
                </>
            )}
            <FinancingModalTriggerBannerMinicart
                closeModal={props.closeModal}
            />
        </div>
    );
};
